import { Mail, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DataTable } from '../../../../components/DataTable';
import type { Column } from '../../../../components/DataTable';
import {
  fetchOutreachPersonLastEmails,
  fetchRetargetingPersons,
  sendOutreachIntroEmails,
  type OutreachPersonLastEmail,
  type OutreachPersonRow,
} from './outreachImportService';
import { EMAIL_STATUS_LABEL, emailStatusChipClass, formatRelativeSent } from './outreachEmailUtils';
import { getPersonDisplayName, getPersonFunctionalArea, getPersonLocation } from './outreachPersonUtils';
import {
  CLOSED_SEND_MODAL,
  OutreachSendEmailModal,
  runOutreachSendWithModal,
  type OutreachSendModalState,
} from './OutreachSendEmailModal';

const STATE_LABEL: Record<string, string> = {
  not_contacted: 'New',
  in_sequence: 'In sequence',
  responded: 'Responded',
  converted: 'Converted',
  do_not_contact: 'Blocked',
  lost: 'Lost',
};

const FETCH_TIMEOUT_MS = 60_000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms)),
  ]);
}

interface RetargetingTabSectionProps {
  isActive: boolean;
}

export default function RetargetingTabSection({ isActive }: RetargetingTabSectionProps) {
  const [persons, setPersons] = useState<OutreachPersonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sendModal, setSendModal] = useState<OutreachSendModalState>(CLOSED_SEND_MODAL);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [lastEmailByPerson, setLastEmailByPerson] = useState<Map<string, OutreachPersonLastEmail>>(new Map());
  const hasLoadedRef = useRef(false);
  const personsRef = useRef(persons);
  personsRef.current = persons;

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent && personsRef.current.length === 0) setLoading(true);
    setError(null);
    try {
      const p = await withTimeout(fetchRetargetingPersons(500), FETCH_TIMEOUT_MS);
      setPersons(p);
      const lastEmails = await withTimeout(
        fetchOutreachPersonLastEmails(p.map(row => row.id)),
        FETCH_TIMEOUT_MS,
      );
      setLastEmailByPerson(lastEmails);
      hasLoadedRef.current = true;
    } catch (e) {
      hasLoadedRef.current = false;
      setError(e instanceof Error ? e.message : 'Could not load retargeting list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;
    if (hasLoadedRef.current) return;
    void load({ silent: true });
  }, [isActive, load]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const sendToSelected = async () => {
    const batch = persons.filter(p => selectedIds.has(p.id) && p.email && p.is_reachable).slice(0, 50).map(p => p.id);
    if (batch.length === 0) {
      setActionMsg('Select reachable contacts with email.');
      return;
    }
    setActionMsg(null);
    const result = await runOutreachSendWithModal(
      batch.length,
      setSendModal,
      () => sendOutreachIntroEmails(batch),
      { confirm: `Send follow-up email to ${batch.length} contact(s)?` },
    );
    if (result?.ok) void load({ silent: true });
  };

  const sending = sendModal.open && sendModal.phase === 'sending';

  const cols = useMemo<Column<OutreachPersonRow>[]>(() => [
    {
      key: 'full_name',
      header: 'Name',
      minWidth: 200,
      alwaysVisible: true,
      cell: (_v, row) => {
        const display = getPersonDisplayName(row);
        return (
          <span className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedIds.has(row.id)}
              onChange={() => toggleSelect(row.id)}
              onClick={e => e.stopPropagation()}
              className="shrink-0 w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer"
              aria-label={`Select ${display}`}
            />
            <span className="font-medium text-[13px] truncate">{display}</span>
          </span>
        );
      },
    },
    { key: 'email', header: 'Email', minWidth: 200, cell: (_v, row) => row.email ?? '—' },
    {
      key: 'last_emailed_at',
      header: 'Last outreached',
      minWidth: 140,
      cell: (_v, row) => (
        <span className="text-[12px] tabular-nums whitespace-nowrap" style={{ color: 'var(--vscode-text-secondary)' }}>
          {formatRelativeSent(row.last_emailed_at)}
        </span>
      ),
    },
    {
      key: 'last_email_status',
      header: 'Email status',
      minWidth: 100,
      cell: (_v, row) => {
        const last = lastEmailByPerson.get(row.id);
        if (!last) return '—';
        return (
          <span className={`ds-cp-meta-chip text-[10px] ${emailStatusChipClass(last.last_email_status)}`}>
            {EMAIL_STATUS_LABEL[last.last_email_status] ?? last.last_email_status}
          </span>
        );
      },
    },
    { key: 'functional_area', header: 'Area', minWidth: 120, cell: (_v, row) => getPersonFunctionalArea(row) },
    { key: 'location', header: 'Location', minWidth: 120, cell: (_v, row) => getPersonLocation(row) },
    {
      key: 'outreach_state',
      header: 'Outreach',
      minWidth: 100,
      cell: (_v, row) => (
        <span className="ds-cp-meta-chip text-[10px]">{STATE_LABEL[row.outreach_state] ?? row.outreach_state}</span>
      ),
    },
  ], [selectedIds, toggleSelect, lastEmailByPerson]);

  return (
    <>
    <div className="ds-cp-tab-panel flex flex-col gap-0">
      <p className="text-[11px] mb-2 px-1" style={{ color: 'var(--vscode-text-tertiary)' }}>
        Contacts who received a system email 7+ days ago and are due for a follow-up.
      </p>
      {actionMsg && (
        <p className="text-[12px] mb-2 px-3 py-2 rounded-lg"
           style={{ background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-secondary)' }}>
          {actionMsg}
        </p>
      )}
      {error && (
        <p className="text-[12px] mb-2 px-3 py-2 rounded-lg"
           style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c' }}>{error}</p>
      )}
      <div className="ds-cp-toolbar flex flex-wrap items-center gap-0 mb-0">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none pl-1">
            <input
              type="checkbox"
              checked={selectedIds.size === persons.length && persons.length > 0}
              ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < persons.length; }}
              onChange={() => (selectedIds.size === persons.length
                ? setSelectedIds(new Set())
                : setSelectedIds(new Set(persons.map(p => p.id))))}
              className="w-3.5 h-3.5 rounded accent-purple-600"
            />
          </label>
          <span className="ds-cp-toolbar-count">{persons.length} due for retarget</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          {selectedIds.size > 0 && (
            <button
              type="button"
              disabled={sending}
              onClick={() => void sendToSelected()}
              className="exa-toolbar-btn exa-toolbar-btn--success"
              title="Send follow-up email (max 50)"
            >
              <Mail className="w-3.5 h-3.5" />
              Send email ({selectedIds.size})
            </button>
          )}
          <button type="button" onClick={() => void load({ silent: persons.length > 0 })} disabled={loading} className="exa-toolbar-btn" title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      <DataTable<OutreachPersonRow>
        columns={cols}
        rows={persons}
        loading={false}
        pageSizeOptions={[10, 25, 50]}
        initialPageSize={25}
        reserveFloatingSpace
        ariaLabel="Retargeting contacts"
        emptyMessage="No contacts due for retarget yet. Send intro emails from Outreach — they appear here 7 days later."
      />
    </div>
    <OutreachSendEmailModal
      state={sendModal}
      onClose={() => setSendModal(CLOSED_SEND_MODAL)}
    />
    </>
  );
}
